'use client'
import * as React from 'react'
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { trackGenerateLead } from '@/lib/analytics/ga4'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'

type Intent = 'try-tool' | 'demo' | 'pricing' | 'partnership' | 'newsletter' | 'other'

const INTENT_LABEL: Record<Intent, string> = {
  'try-tool': 'I want to try a tool',
  demo: 'Book a demo',
  pricing: 'Get custom pricing',
  partnership: 'Partner / agency',
  newsletter: 'Subscribe to the newsletter',
  other: 'Something else',
}

export function LeadCaptureForm({
  source,
  toolSlug,
  defaultIntent = 'try-tool',
  variant = 'default',
  /** Optional render slot for the post-submission state. Used by the
   *  /downloads/[slug] route to deliver the gated asset link inline. */
  successContent,
  /** Optional callback fired once the lead has been accepted by the
   *  /api/leads endpoint. Used by the PDP audit tool to unlock the
   *  gated rows in-place, and by callers (e.g. the sales-proposal page)
   *  that need the submitted contact details for a dual-submit to a
   *  second endpoint. Receiving code should treat every field as
   *  optional — existing zero-arg callers (`() => ...`) keep working
   *  unchanged since they simply ignore the argument. */
  onSuccess,
  /** Drop the form's own card chrome (border + bg + padding) so it can sit
   *  inside a wrapper that already provides a panel, without a double frame. */
  bare = false,
}: {
  source: string
  toolSlug?: string
  defaultIntent?: Intent
  variant?: 'default' | 'compact'
  successContent?: React.ReactNode
  onSuccess?: (data: { email: string; name?: string; company?: string; message?: string }) => void
  bare?: boolean
}) {
  const chrome = bare ? '' : cn('rounded-xl border bg-card', variant === 'compact' ? 'p-4' : 'p-6')
  const [state, setState] = React.useState<'idle' | 'submitting' | 'ok' | 'error'>('idle')
  const [message, setMessage] = React.useState('')
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setState('submitting')
    setMessage('')
    const fd = new FormData(e.currentTarget)
    const payload = {
      email: String(fd.get('email') || ''),
      name: String(fd.get('name') || ''),
      company: String(fd.get('company') || ''),
      intent: String(fd.get('intent') || defaultIntent),
      message: String(fd.get('message') || ''),
      company_url: String(fd.get('company_url') || ''), // honeypot
      'cf-turnstile-response': turnstileToken,
      source,
      toolSlug,
    }
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) {
        setState('error')
        setMessage(json?.error || 'Something went wrong. Try again or email hi@idukki.io.')
        return
      }
      setState('ok')
      trackGenerateLead({ intent: payload.intent, source: payload.source })
      onSuccess?.({ email: payload.email, name: payload.name, company: payload.company, message: payload.message })
    } catch {
      setState('error')
      setMessage('Network error. Try again or email hi@idukki.io.')
    }
  }

  if (state === 'ok') {
    if (successContent) {
      return <div className={cn(chrome || 'p-1')}>{successContent}</div>
    }
    return (
      <div className={cn(chrome, 'text-center')}>
        <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
        <p className="mt-3 font-display text-lg font-medium">Thanks, we’ll be in touch.</p>
        <p className="mt-1 text-sm text-muted-foreground">A real human will reply within one business day. In a hurry? <a className="underline" href="https://app.idukki.io/register">Spin up a free account</a>.</p>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className={chrome || undefined}>
      {/* Honeypot, bots fill in everything visible. Real humans skip this. */}
      <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden>
        <label>
          Company website
          <input type="text" name="company_url" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <fieldset disabled={state === 'submitting'} className="m-0 grid min-w-0 gap-3 border-0 p-0 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Email</span>
          <Input type="email" name="email" required placeholder="you@brand.com" className="mt-1" />
        </label>
        <label className="text-sm">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</span>
          <Input type="text" name="name" placeholder="Your name" className="mt-1" />
        </label>
        <label className="text-sm sm:col-span-1">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Company</span>
          <Input type="text" name="company" placeholder="Brand or agency" className="mt-1" />
        </label>
        <label className="text-sm sm:col-span-1">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Intent</span>
          <select
            name="intent"
            defaultValue={defaultIntent}
            className="mt-1 h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {(Object.keys(INTENT_LABEL) as Intent[]).map((k) => (
              <option key={k} value={k}>{INTENT_LABEL[k]}</option>
            ))}
          </select>
        </label>
        {variant !== 'compact' && (
          <label className="text-sm sm:col-span-2">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Anything else? (optional)</span>
            <textarea
              name="message"
              rows={3}
              className="mt-1 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="What are you trying to ship? Volumes, channels, deadline…"
            />
          </label>
        )}
      </fieldset>
      {turnstileSiteKey && (
        <div className="mt-4 max-w-[320px]">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> Cloudflare bot-protection
          </p>
          <TurnstileWidget size="normal" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
        </div>
      )}
      <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">No spam. Unsubscribe anytime. We never sell your data.</p>
        <Button
          type="submit"
          disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)}
          className="sm:w-auto"
        >
          {state === 'submitting' ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>) : 'Talk to us'}
        </Button>
      </div>
      {state === 'error' && message && (
        <p role="alert" className="mt-3 text-xs text-destructive">{message}</p>
      )}
    </form>
  )
}
